import React from "react";
import ClickableBatch from "../../../clickableBatch/clickableBatch";

/** Cell value shape for {@link ClickableBadgeWithOnClickEmitter}. */
interface ClickableBadgeWithOnClickEmitterValue {
  /** Row/order identifier emitted back to the table consumer on click. */
  id: string;
  /** Text rendered inside the clickable badge. */
  value: string;
  /**
   * When true, renders a red asterisk (`*`) after the badge to mark the
   * underlying field as required.
   */
  isRequired?: boolean;
  /**
   * When true, renders the value as a plain truncated string instead of the
   * clickable badge.
   */
  isStatic?: boolean;
}

/** Props for {@link ClickableBadgeWithOnClickEmitter}. */
interface ClickableBadgeWithOnClickEmitterProps {
  /** Cell value carrying the identifier and the badge label. */
  value: ClickableBadgeWithOnClickEmitterValue;
  /**
   * Optional click handler. When provided, it is invoked with the cell's
   * `id` so a parent `CustomTable` consumer can react to the badge click.
   */
  onClick?: (id: string) => void;
  /**
   * When true, renders a red asterisk (`*`) after the badge to mark the
   * underlying field as required. Takes precedence over
   * `value.isRequired`.
   */
  required?: boolean;
  /**
   * When true, renders the cell's value as a plain truncated string instead
   * of the clickable badge (no chevron, no click emitted to `onClick`).
   * Takes precedence over `value.isStatic`.
   */
  isStatic?: boolean;
}

/**
 * Table cell that renders a clickable badge and emits the row's `id` via an
 * optional `onClick` callback instead of opening a modal itself. The wrapping
 * `div` stops click propagation so the table row's own click handler does not
 * also fire.
 *
 * @param props.value - Cell value with `id` and the badge `value` text.
 * @param props.onClick - Optional handler invoked with the cell `id` on click.
 * @param props.required - When true, appends a red asterisk after the badge.
 * @param props.isStatic - When true, renders the value as a plain truncated
 *   string instead of the clickable badge.
 */
export const ClickableBadgeWithOnClickEmitter: React.FC<
  ClickableBadgeWithOnClickEmitterProps
> = ({ value, onClick, required, isStatic }) => {
  const showRequiredMark = Boolean(required || value?.isRequired);
  const showAsStatic = Boolean(isStatic || value?.isStatic);
  return (
    <div
      className="table-cell-clickable-badge w-full"
      style={
        showRequiredMark
          ? { display: "flex", alignItems: "center", gap: "0.25rem" }
          : undefined
      }
      onClick={(e) => e.stopPropagation()}
    >
      {showAsStatic ? (
        <span
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {value.value}
        </span>
      ) : (
        <ClickableBatch
          text={value.value}
          onClick={() => onClick?.(value.id)}
        />
      )}
      {showRequiredMark && (
        <span
          aria-hidden="true"
          style={{
            color: "red",
            fontSize: "0.75rem",
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          *
        </span>
      )}
    </div>
  );
};

export default ClickableBadgeWithOnClickEmitter;
